import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '@shared/config/axios';
import { AxiosErrorAlertHandler } from '@shared/api/utils';
import { ORDER_STATUSES, PACK_STATUSES } from '@shared/api/constants';

const API_KEY_NP = '5b5e1fd2312a7332fdf941819a92e685';

const getPackKey = (pack) =>
    `${pack.weight}_${pack.grind}_${pack.roastType._id}_${pack.country._id}_${pack.producerName}`;


export const useOrder = (orderId) => {
    const queryClient = useQueryClient();
    const alertRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState('');

    const [scannedPacks, setScannedPacks] = useState([]);

    console.log(scannedPacks);

    const { data, refetch, isLoading } = useQuery({
        queryKey: ['order', orderId],
        queryFn: async () => await axios.get(`/orders/${orderId}`).then(({ data }) => data),
        refetchOnWindowFocus: true,
        retry: false,
    });

    useEffect(() => {
        if (data) {
            setOrder({ ...data, packs: groupPacksByFields(data.packs) });
        }

        if (orderId) {
            localStorage.setItem('lastSelectedOrderId', orderId);
        }
        // eslint-disable-next-line
    }, [data, orderId]);

    const mutationConfirmAssembly = useMutation({
        mutationFn: async (updatedData) => {
            return await axios.patch(`/orders/${orderId}/`, updatedData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['order', orderId]); // Это обновит данные в кэше
            refetch();
            setLoading(false);
        },
        onError: (error) => {
            AxiosErrorAlertHandler(error, alertRef);
            setLoading(false);
        },
        retry: false
    });

    const mutationCreateConsignmentNote = useMutation({
        mutationFn: async () => {
            return await axios.post(`/orders/create-consignment-note/${orderId}/`).then(({ data }) => data);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['order', orderId]); // Это обновит данные в кэше
            refetch();
            setLoading(false);
            const url = `https://my.novaposhta.ua/orders/printMarking100x100/orders[]/${data.Ref}/type/pdf/apiKey/${API_KEY_NP}`;
            window.open(url, '_blank');
        },
        onError: (error) => {
            AxiosErrorAlertHandler(error, alertRef);
            setLoading(false);
        },
        retry: false
    });

    const handleCreateConsignmentNote = async () => {
        setLoading(true);
        mutationCreateConsignmentNote.mutate();
        mutationConfirmAssembly.mutate({
            status: ORDER_STATUSES.COLLECTED,
            packRefs: scannedPacks.map(item => item?._id)
        });
    };

    // Функция группировки
    const groupPacksByFields = useCallback((packs = []) => {
        return Object.values(
            packs.reduce((acc, pack) => {
                const key = getPackKey(pack);
                if (!acc[key]) {
                    acc[key] = { ...pack, count: 1 };
                } else {
                    acc[key].count += 1;
                }
                return acc;
            }, {})
        );
    }, []);

    const uniquePacks = groupPacksByFields(scannedPacks);

    // Выносим packs заказа в удобную переменную
    const orderPacks = order?.packs || [];

    // ---- Запрос для получения данных одного pack
    const fetchPackById = async (packId) => {
        return axios.get(`/packs/${packId}`).then((res) => res.data);
    };

    // ---- Добавляем логику проверки:
    // 1) Пак вообще есть в заказе?
    // 2) Не превышено ли нужное количество?
    const addScannedPack = async (packId) => {
        try {
            const packData = await fetchPackById(packId);
            // Получаем «ключ» отсканированного
            const scannedKey = getPackKey(packData);

            // (1) Проверяем, не сканировали ли уже эту физическую упаковку
            const alreadyScanned = scannedPacks.some((p) => p._id === packData._id);
            if (alreadyScanned) {
                alertRef.current?.handleOpen('warning', 'the packaging has already been scanned');
                return;
            }

            if (!packData?.status || packData?.status !== PACK_STATUSES.COMPLETED) {
                alertRef.current?.handleOpen('warning', 'action not available for current package status');
                return;
            }

            // Проверяем, есть ли вообще такой товар в заказе
            const neededPack = orderPacks.find((p) => getPackKey(p) === scannedKey);
            if (!neededPack) {
                // Нет такого пака в заказе — не добавляем
                alertRef.current?.handleOpen('warning', 'this product is not in the order list');
                return;
            }

            // Считаем, сколько уже отсканировано
            const currentCountScanned = scannedPacks.filter((p) => getPackKey(p) === scannedKey).length;
            // Сколько нужно в заказе
            const neededCount = neededPack.count;

            if (currentCountScanned >= neededCount) {
                // Уже не нужно добавлять
                alertRef.current?.handleOpen('info', 'the required quantity of this product has already been scanned');
                return;
            }

            // Если всё ок — добавляем
            setScannedPacks((prev) => [...prev, packData]);
        } catch (error) {
            AxiosErrorAlertHandler(error, alertRef);
        }
    };

    // Удаляем все паки одного «ключа» (одной номенклатуры) из scannedPacks
    const removePackFromScanned = (packToRemove) => {
        setScannedPacks((prev) => {
            const removeKey = getPackKey(packToRemove);
            return prev.filter((item) => getPackKey(item) !== removeKey);
        });
    };

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${day}-${month}-${year}`;
    };

    return {
        alertRef,
        order, setOrder,
        loading, isLoading,
        handleCreateConsignmentNote,
        formatDate,
        groupPacksByFields,
        scannedPacks,
        uniquePacks,
        addScannedPack,
        removePackFromScanned
    };
};